import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  AuthApi,
  IApiError,
  isLoggedInAtom,
  isLoginInProgressAtom,
  Log,
  loginErrorsAtom,
  loginFlowStepAtom,
  LoginFormFlow,
  mfaChallengeTokenAtom,
  normalizeErrors,
  ProfileStorage,
  RecaptchaEnterpriseActions,
  updateTokensAndReconnectSocket,
} from "@ryde/shared";

import { useSimpleNavigation } from "../../navigation";
import { useRecaptchaToken } from "./use-recaptcha";

interface IUseMfaLoginResult {
  mfaVerify: (code: string) => Promise<void>;
  mfaResend: () => Promise<boolean>;
}

export const useMfaLogin = (): IUseMfaLoginResult => {
  const { t } = useTranslation();
  const setLoginErrors = useSetRecoilState(loginErrorsAtom);
  const setLoggingInProgress = useSetRecoilState(isLoginInProgressAtom);
  const setIsLoggedIn = useSetRecoilState(isLoggedInAtom);
  const setLoginFlowStep = useSetRecoilState(loginFlowStepAtom);
  const setMfaChallengeToken = useSetRecoilState(mfaChallengeTokenAtom);
  const mfaChallengeToken = useRecoilValue(mfaChallengeTokenAtom);
  const { navigateToHomeOrLoginReturnUrl } = useSimpleNavigation();
  const { getRecaptchaToken } = useRecaptchaToken();

  const mfaVerify = async (code: string): Promise<void> => {
    if (!mfaChallengeToken) {
      Log.warn("MFA verify attempted without challenge token");
      setLoginFlowStep(LoginFormFlow.GENERIC_STEP);
      return;
    }

    try {
      setLoginErrors([]);
      setLoggingInProgress(true);

      const recaptchaToken = await getRecaptchaToken(
        RecaptchaEnterpriseActions.MFA_VERIFY,
      );

      if (!recaptchaToken) {
        return;
      }

      const result = await AuthApi.mfaVerify({
        mfaChallengeToken,
        code,
        recaptchaToken,
      });

      if (!result?.success || !result.data) {
        const errorsNormalized = normalizeErrors(result?.error, t);
        setLoginErrors(errorsNormalized);
        return;
      }

      const profile = result.data;

      Log.debug("Session established for: ", profile);

      ProfileStorage.prepareScopeForUserLogin(profile.id);

      setMfaChallengeToken(undefined);
      setLoginFlowStep(LoginFormFlow.GENERIC_STEP);
      updateTokensAndReconnectSocket({
        accessToken: null,
        refreshToken: null,
      });
      setIsLoggedIn(true);
      navigateToHomeOrLoginReturnUrl();
    } catch (error) {
      const errorsNormalized = normalizeErrors(error as IApiError, t);
      setLoginErrors(errorsNormalized);
      Log.warnAndToast(t("errors.authenticationError"), error);
    } finally {
      setLoggingInProgress(false);
    }
  };

  const mfaResend = async (): Promise<boolean> => {
    if (!mfaChallengeToken) {
      Log.warn("MFA resend attempted without challenge token");
      setLoginFlowStep(LoginFormFlow.GENERIC_STEP);
      return false;
    }

    try {
      setLoginErrors([]);

      const recaptchaToken = await getRecaptchaToken(
        RecaptchaEnterpriseActions.MFA_RESEND,
      );

      if (!recaptchaToken) {
        return false;
      }

      const result = await AuthApi.mfaResend({
        mfaChallengeToken,
        recaptchaToken,
      });

      if (!result?.success) {
        const errorsNormalized = normalizeErrors(result?.error, t);
        setLoginErrors(errorsNormalized);
        return false;
      }

      return true;
    } catch (error) {
      const errorsNormalized = normalizeErrors(error as IApiError, t);
      setLoginErrors(errorsNormalized);
      Log.warnAndToast(t("errors.authenticationError"), error);
      return false;
    }
  };

  return { mfaVerify, mfaResend };
};
